import { Paperclip, RotateCcw, Trash2 } from "lucide-react";
import { type LogEntry, searchModeLabel } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import {
  fmtKst,
  fmtMs,
  fmtModel,
  cn,
  inputFiles,
  questionText,
} from "@/lib/utils";

interface Props {
  items: LogEntry[];
  loading: boolean;
  onRowClick: (item: LogEntry) => void;
  selectedId?: string;
  slowThresholdMs?: number;
  /** 선택된 query_id 집합. onToggleSelect 를 넘길 때만 의미가 있다. */
  selectedIds?: Set<string>;
  /** 넘기면 선택 체크박스 + 행별 삭제/복원 열이 표시된다(운영포털 이력 삭제). */
  onToggleSelect?: (queryId: string) => void;
  /** 헤더 체크박스 — 현재 페이지 전체 선택/해제. */
  onToggleAll?: (checked: boolean) => void;
  onDelete?: (item: LogEntry) => void;
  onRestore?: (item: LogEntry) => void;
}

export function LogTable({
  items,
  loading,
  onRowClick,
  selectedId,
  slowThresholdMs = 10_000,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onDelete,
  onRestore,
}: Props) {
  // 선택 기능은 핸들러가 주어질 때만 켠다 — 기존 읽기 전용 사용처는 그대로 동작.
  const selectable = Boolean(onToggleSelect);
  const checked = selectedIds ?? new Set<string>();
  const allChecked = items.length > 0 && items.every((it) => checked.has(it.query_id));
  const someChecked = items.some((it) => checked.has(it.query_id));

  if (loading) {
    return (
      <div className="rounded-md border p-12 text-center text-sm text-muted-foreground">
        로딩 중…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-md border p-12 text-center text-sm text-muted-foreground">
        조건에 맞는 결과가 없습니다.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
          <tr>
            {selectable && (
              <th className="w-[40px] px-3 py-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 align-middle"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = someChecked && !allChecked;
                  }}
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                  aria-label="현재 목록 전체 선택"
                />
              </th>
            )}
            <Th className="w-[150px]">시각 (KST)</Th>
            <Th className="w-[120px]">사용자</Th>
            <Th>질문</Th>
            <Th className="w-[80px] text-center">답변</Th>
            <Th className="w-[100px]">모델</Th>
            <Th className="w-[100px]">검색</Th>
            <Th className="w-[90px] text-right">응답</Th>
            <Th className="w-[80px] text-center">KMS</Th>
            <Th className="w-[80px] text-center">피드백</Th>
            {selectable && <Th className="w-[56px] text-center">관리</Th>}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const isUnanswered = !it.was_answered;
            const isSlow =
              it.response_ms !== null && it.response_ms >= slowThresholdMs;
            const selected = selectedId === it.query_id;
            const isDeleted = Boolean(it.deleted);
            // 요청 문구(없으면 첨부 파일명으로 대체) + 사용자가 보낸 첨부 목록
            const qText = questionText(it.question_summary, it.files);
            const attached = inputFiles(it.files);
            const attachTitle = attached.map((f) => f.name).join(", ");
            return (
              <tr
                key={it.id}
                onClick={() => onRowClick(it)}
                className={cn(
                  "cursor-pointer border-b transition-colors hover:bg-accent/40",
                  selected && "bg-primary/5",
                  isUnanswered && "bg-red-50/60 hover:bg-red-100/60",
                  !isUnanswered && isSlow && "bg-orange-50/60 hover:bg-orange-100/60",
                  // 삭제(숨김)된 행은 '삭제 포함 보기' 에서만 나타난다 — 회색 처리
                  isDeleted && "bg-muted/50 text-muted-foreground hover:bg-muted/70",
                )}
              >
                {selectable && (
                  <td className="px-3 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 align-middle"
                      checked={checked.has(it.query_id)}
                      onChange={() => onToggleSelect?.(it.query_id)}
                      aria-label={`${qText} 선택`}
                    />
                  </td>
                )}
                <Td className="tabular-nums text-xs">{fmtKst(it.timestamp)}</Td>
                <Td className="text-xs">
                  <div className="font-medium">{it.user_name ?? "-"}</div>
                  <div className="text-muted-foreground">{it.user_id ?? ""}</div>
                </Td>
                <Td className="max-w-0">
                  <div className={cn("truncate", isDeleted && "line-through")} title={qText}>
                    {qText}
                  </div>
                  {isDeleted && (
                    <div className="mt-0.5 text-xs text-muted-foreground">삭제됨</div>
                  )}
                  {attached.length > 0 && (
                    <div
                      className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"
                      title={attachTitle}
                    >
                      <Paperclip className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {attached[0].name}
                        {attached.length > 1 && ` 외 ${attached.length - 1}건`}
                      </span>
                    </div>
                  )}
                </Td>
                <Td className="text-center">
                  {it.was_answered ? (
                    <Badge variant="success">답변</Badge>
                  ) : (
                    <Badge variant="destructive">미답변</Badge>
                  )}
                </Td>
                <Td className="text-xs text-muted-foreground" title={it.model ?? undefined}>
                  {fmtModel(it.model)}
                </Td>
                <Td className="text-xs text-muted-foreground" title={it.search_mode ?? undefined}>
                  {searchModeLabel(it.search_mode)}
                </Td>
                <Td
                  className={cn(
                    "text-right tabular-nums",
                    isSlow && "text-orange-600 font-semibold",
                  )}
                >
                  {fmtMs(it.response_ms)}
                </Td>
                <Td className="text-center">
                  {it.was_saved ? <span className="text-green-600">✓</span> : <span className="text-muted-foreground">·</span>}
                </Td>
                <Td className="text-center">
                  {it.feedback === 1 && <span title="좋음">👍</span>}
                  {it.feedback === -1 && <span title="나쁨">👎</span>}
                  {it.feedback === null && <span className="text-muted-foreground">·</span>}
                </Td>
                {selectable && (
                  <td className="px-3 py-2 text-center align-top" onClick={(e) => e.stopPropagation()}>
                    {isDeleted ? (
                      <button
                        type="button"
                        onClick={() => onRestore?.(it)}
                        title="복원"
                        aria-label="이력 복원"
                        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onDelete?.(it)}
                        title="삭제"
                        aria-label="이력 삭제"
                        className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <th className={cn("px-3 py-2 text-left font-medium", className)}>
      {children}
    </th>
  );
}
function Td({ className, children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn("px-3 py-2 align-top", className)} {...props}>
      {children}
    </td>
  );
}
